import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { PackageCard } from "@/components/PackageCard";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/apiClient";
import { CATEGORY_LABELS } from "@/data/site";
import useSeo from "@/lib/useSeo";

export default function Packages() {
  useSeo({ title: "Journeys & Tour Packages", description: "Browse VacanzaGo's curated pilgrimage, Himalayan, leisure, heritage and offbeat journeys across India. Filter by destination, type and duration." });
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ categories: [], regions: [], tour_types: [] });
  const [loading, setLoading] = useState(true);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const region = params.get("region") || "";
  const tour_type = params.get("tour_type") || "";

  useEffect(() => {
    api.get("/itineraries/meta/filters").then((r) => setFilters(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/itineraries", { params: { published: true, q: q || undefined, category: category || undefined, region: region || undefined, tour_type: tour_type || undefined } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [q, category, region, tour_type]);

  const set = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  return (
    <div className="pt-28 pb-24 bg-[#FFF7EC] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal className="max-w-3xl mb-10">
          <p className="eyebrow">All journeys</p>
          <h1 className="font-display text-5xl sm:text-6xl font-medium tracking-tight mt-3 text-[#2A1808]">Find your journey</h1>
          <p className="mt-4 text-[#553204] text-lg">We operate many more pan-India tours than listed. Don't see it? Tell us and we'll craft it.</p>
        </Reveal>

        {/* Filters */}
        <div className="bg-[#FFFBF5] border border-[#553204]/10 rounded-md p-4 sm:p-5 mb-10">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A6B47]" />
              <input
                data-testid="packages-search-input"
                defaultValue={q}
                onKeyDown={(e) => { if (e.key === "Enter") set("q", e.currentTarget.value); }}
                onBlur={(e) => set("q", e.target.value)}
                placeholder="Search destinations, themes, e.g. honeymoon Kashmir…"
                className="w-full rounded-full border border-[#553204]/15 bg-white pl-10 pr-4 py-2.5 text-sm outline-none focus:border-[#F49E1A]"
              />
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <SlidersHorizontal size={16} className="text-[#8A6B47] hidden sm:block" />
              <select data-testid="filter-category" value={category} onChange={(e) => set("category", e.target.value)} className="rounded-full border border-[#553204]/15 bg-white px-4 py-2 text-sm outline-none focus:border-[#F49E1A]">
                <option value="">All experiences</option>
                {filters.categories.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
              </select>
              <select data-testid="filter-region" value={region} onChange={(e) => set("region", e.target.value)} className="rounded-full border border-[#553204]/15 bg-white px-4 py-2 text-sm outline-none focus:border-[#F49E1A]">
                <option value="">All regions</option>
                {filters.regions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select data-testid="filter-tour-type" value={tour_type} onChange={(e) => set("tour_type", e.target.value)} className="rounded-full border border-[#553204]/15 bg-white px-4 py-2 text-sm outline-none focus:border-[#F49E1A] capitalize">
                <option value="">All types</option>
                {filters.tour_types.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
              </select>
              {(q || category || region || tour_type) && (
                <button data-testid="clear-filters" onClick={() => setParams(new URLSearchParams())} className="text-sm text-[#F49E1A] font-semibold px-2">Clear</button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-[#8A6B47]">Loading journeys…</p>
        ) : items.length === 0 ? (
          <p className="text-[#8A6B47]" data-testid="no-results">No journeys match those filters. Try clearing filters or use the planner.</p>
        ) : (
          <>
            <p className="text-sm text-[#8A6B47] mb-6" data-testid="results-count">{items.length} journeys</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {items.map((item) => (
                <Reveal key={item.slug}><PackageCard item={item} /></Reveal>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
